import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { MeResponse, PermissionKey } from "@theadam/shared";
import { ApiError, api, setCsrfToken } from "./api";

export const ME_KEY = ["me"] as const;

/** Oturum süresi dolduysa giriş ekranı bir kez bilgi verir. */
let sessionExpired = false;
export const markSessionExpired = () => {
  sessionExpired = true;
};
export const isSessionExpired = () => sessionExpired;
export const clearSessionExpired = () => {
  sessionExpired = false;
};

export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        const me = await api.get<MeResponse>("/auth/me", { timeoutMs: 10_000 });
        setCsrfToken(me.csrfToken);
        return me;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          setCsrfToken(null);
          return null;
        }
        throw err;
      }
    },
    staleTime: 60_000,
    // Ağ aksaklığında (bağlantı takıldı, zaman aşımı, 5xx) oturum kontrolü birkaç kez yeniden denenir;
    // tek bir aksaklık paneli "sunucuya ulaşılamadı" ekranında bırakmasın. 4xx yeniden denenmez.
    retry: (count, err) => count < 3 && (!(err instanceof ApiError) || err.status === 0 || err.status >= 500),
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    // Hata ekranındayken 15 saniyede bir kendiliğinden yeniden denenir.
    refetchInterval: (q) => (q.state.status === "error" ? 15_000 : false),
  });
}

export function useCan() {
  const { data } = useMe();
  return (permission: PermissionKey) => Boolean(data?.permissions.includes(permission));
}

export function useLogout() {
  const qc = useQueryClient();
  return async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setCsrfToken(null);
      qc.clear();
      window.location.assign("/giris");
    }
  };
}
